import React, { useState, useEffect } from 'react';
import API from '../../../../utils/api';
import { X, Search, UserCheck, Users, AlertCircle } from 'lucide-react';
import './SeleccionMeseroHorizontal.css';


/**
 * Componente para seleccionar un mesero de una cuadrícula
 * @param {Function} onMeseroSelect - Función llamada cuando se selecciona un mesero
 * @param {Function} onCancel - Función llamada cuando se cancela la selección
 * @param {Object} meseroActual - Datos del mesero actualmente seleccionado (opcional)
 */
const SeleccionMeseroHorizontal = ({ onMeseroSelect, onCancel, meseroActual = null }) => {
  const [meseros, setMeseros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('');

  // Cargar los meseros al montar el componente
  useEffect(() => {
    fetchMeseros();
  }, []);

  // Función para obtener la lista de meseros
  const fetchMeseros = async () => {
    try {
      setLoading(true);
      setError(null);

      // Llamar a la API para obtener los meseros
      const meserosData = await API.meseros.getAll();
      setMeseros(meserosData);
    } catch (err) {
      console.error("Error al cargar meseros:", err);
      setError("No se pudieron cargar los meseros. Usando datos de ejemplo.");

      // Datos de demostración en caso de error
      const mockMeseros = [

      ];

      setMeseros(mockMeseros);
    } finally {
      setLoading(false);
    }
  };

  // Genera un color basado en la primera letra del nombre
  const getColorForName = (nombre) => {
    if (!nombre || nombre.length === 0) return '#2196F3'; // Color por defecto

    // Lista de colores para asignar
    const colors = [
      '#F44336', // Rojo
      '#E91E63', // Rosa
      '#9C27B0', // Púrpura
      '#673AB7', // Púrpura oscuro
      '#3F51B5', // Índigo
      '#2196F3', // Azul
      '#03A9F4', // Azul claro
      '#00BCD4', // Cian
      '#009688', // Verde azulado
      '#4CAF50', // Verde
      '#8BC34A', // Verde claro
      '#CDDC39', // Lima
      '#FFC107', // Ámbar
      '#FF9800', // Naranja
      '#FF5722', // Naranja oscuro
      '#795548', // Marrón
      '#607D8B'  // Gris azulado
    ];

    // Convertir la primera letra a un valor entre 0 y 16
    const firstChar = nombre.charAt(0).toUpperCase();
    const charCode = firstChar.charCodeAt(0);
    const colorIndex = charCode % colors.length;

    return colors[colorIndex];
  };

  // Filtrar meseros según búsqueda
  const meserosFiltrados = filtro
    ? meseros.filter(m => m.nombre.toLowerCase().includes(filtro.toLowerCase()) ||
      m.numeroMesero.toString().includes(filtro))
    : meseros;

  // Manejar cambio en la entrada de búsqueda
  const handleFiltroChange = (e) => {
    setFiltro(e.target.value);
  };

  // Limpiar el filtro
  const limpiarFiltro = () => {
    setFiltro('');
  };

  return (
    <div className="selector-horizontal-overlay">
      <div className="selector-horizontal-modal">
        {/* Header del modal */}
        <div className="selector-horizontal-header">
          <div className="header-horizontal-title-section">
            <div className="title-horizontal-icon-wrapper">
              <Users size={24} />
            </div>
            <h2 className="selector-horizontal-title">Seleccionar Mesero</h2>
          </div>
          <button className="btn-horizontal-close" onClick={onCancel}>
            <X size={20} />
          </button>
        </div>

        {/* Barra de búsqueda */}
        <div className="search-horizontal-container">
          <div className="search-horizontal-wrapper">
            <Search className="search-horizontal-icon" size={20} />
            <input
              type="text"
              value={filtro}
              onChange={handleFiltroChange}
              placeholder="Buscar por nombre o número..."
              className="search-horizontal-input"
              autoFocus
            />
            {filtro && (
              <button className="clear-horizontal-search" onClick={limpiarFiltro}>
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Contenido del modal */}
        <div className="selector-horizontal-content">
          {loading ? (
            <div className="loading-horizontal-container">
              <div className="loading-horizontal-spinner">
                <div className="spinner-horizontal-circle"></div>
              </div>
              <span className="loading-horizontal-text">Cargando meseros...</span>
            </div>
          ) : error ? (
            <div className="error-horizontal-message">
              <AlertCircle size={24} className="error-horizontal-icon" />
              <div className="error-horizontal-text">{error}</div>
            </div>
          ) : meserosFiltrados.length === 0 ? (
            <div className="no-results-horizontal">
              <Users size={48} className="no-results-horizontal-icon" />
              <p>No se encontraron meseros con ese criterio de búsqueda</p>
            </div>
          ) : (
            <div className="meseros-horizontal-grid">
              {meserosFiltrados.map((mesero) => {
                // Verificar si este mesero está actualmente seleccionado
                const isSelected = meseroActual && meseroActual.idMesero === mesero.idMesero;

                return (
                  <div
                    key={mesero.idMesero}
                    className={`mesero-horizontal-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => onMeseroSelect(mesero)}
                    style={{ '--card-color': getColorForName(mesero.nombre) }}
                  >
                    <div className="mesero-horizontal-avatar">
                      <UserCheck size={24} />
                    </div>
                    <div className="mesero-horizontal-info">
                      <span className="mesero-horizontal-name">{mesero.nombre}</span>
                      <span className="mesero-horizontal-number">#{mesero.numeroMesero}</span>
                    </div>
                    {isSelected && (
                      <div className="selected-horizontal-badge">
                        <UserCheck size={16} />
                      </div>
                    )}
                    <div className="mesero-horizontal-hover-effect"></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer del modal */}
        <div className="selector-horizontal-footer">
          <button className="btn-horizontal-cancel" onClick={onCancel}>
            <X size={18} />
            <span>Cancelar</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SeleccionMeseroHorizontal;